import React from 'react';
import { captureComponentCrash } from '../../lib/posthog';
import { ClientErrorPage } from './client-error-page';

type AppErrorBoundaryProps = {
  children: React.ReactNode;
};

type CaughtError = {
  message: string;
  name: string;
  stack: string | null;
};

type AppErrorBoundaryState = {
  hasError: boolean;
  error: CaughtError | null;
};

function toCaughtError(error: unknown): CaughtError {
  if (error instanceof Error) {
    return {
      message: error.message || 'Unknown error',
      name: error.name || 'Error',
      stack: error.stack || null,
    };
  }

  return {
    message: String(error ?? 'Unknown error'),
    name: 'Error',
    stack: null,
  };
}

export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  private hasReported = false;

  state: AppErrorBoundaryState = {
    hasError: false,
    error: null,
  };

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return {
      hasError: true,
      error: toCaughtError(error),
    };
  }

  componentDidCatch(error: Error) {
    if (this.hasReported) {
      return;
    }

    this.hasReported = true;
    captureComponentCrash(error);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <ClientErrorPage
          message={this.state.error.message}
          name={this.state.error.name}
          stack={this.state.error.stack}
        />
      );
    }

    return this.props.children;
  }
}
